import * as CookieManager from "@/lib/managers/CookieManager";
import { ModeAwareStorageManager, modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';
import { directCookieAccess } from "@/lib/storage/StorageAdapter";
import { sharedSequenceStateManager } from "./SharedSequenceStateManager";
import { OverlayStateCoordinator } from "./OverlayStateCoordinator";

interface OverlayState {
  showInProgress: boolean;
  showGameCompletion: boolean;
  immediateCompletion: boolean;
  redirectToWelcome: boolean;
  showInstructions?: boolean;
  showStreak?: boolean;
}

const getOverlayStateFromCookies = (): OverlayState => {
  // Use mode-aware storage for overlay states
  const inProgressOverlayShown = modeAwareStorage.getCookie('in_progress_overlay_shown') === 'true';
  const gameCompletionState = modeAwareStorage.getCookie('game_completion_state') === 'true';
  const immediateCompletion = modeAwareStorage.getCookie('immediate_completion') === 'true';
  const redirectToWelcome = modeAwareStorage.getCookie('redirect_to_welcome') === 'true';

  return {
    showInProgress: !inProgressOverlayShown,
    showGameCompletion: gameCompletionState,
    immediateCompletion: immediateCompletion,
    redirectToWelcome: redirectToWelcome,
    showInstructions: false,
    showStreak: false
  };
};

const getOverlayStateFromLocalStorage = (): OverlayState => {
  const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
  const prefix = currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_';
  const inProgressOverlayShown = localStorage.getItem(`${prefix}in_progress_overlay_shown`) === 'true';
  const gameCompletionState = localStorage.getItem(`${prefix}game_completion_state`) === 'true';

  return {
    showInProgress: !inProgressOverlayShown,
    showGameCompletion: gameCompletionState,
    immediateCompletion: false, // Local storage doesn't handle immediate completion
    redirectToWelcome: false, // Local storage doesn't handle redirect
    showInstructions: false,
    showStreak: false
  };
};

/**
 * Checks if there's an in-progress game for a given mode
 * DEPRECATED: Use OverlayStateCoordinator.captureStateSnapshot() instead
 * @param mode The game mode to check ('daily' or 'practice')
 * @returns True if there's an in-progress game
 */
const hasInProgressGame = (mode: 'daily' | 'practice' = 'daily'): boolean => {
  console.warn('hasInProgressGame() is deprecated. Use OverlayStateCoordinator instead.');
  try {
    // Use the directCookieAccess utility method which checks for current_word and game_status
    const hasGameInCookies = directCookieAccess.hasInProgressGame(mode);
    
    console.log(`OverlayManager: hasInProgressGame check for ${mode}:`, {
      hasGameInCookies,
      timestamp: new Date().toISOString()
    });
    
    return hasGameInCookies;
  } catch (error) {
    console.error('Error checking for in-progress game:', error);
    return false;
  }
};

/**
 * Checks if we're in a page reload scenario
 * DEPRECATED: Use OverlayStateCoordinator.captureStateSnapshot() instead
 * @returns True if this appears to be a page reload
 */
const isPageReload = (): boolean => {
  console.warn('isPageReload() is deprecated. Use OverlayStateCoordinator instead.');
  try {
    // Check if this is a first load in this session (READ-ONLY, no side effects)
    const isFirstLoad = sessionStorage.getItem('babblegram_page_loaded') !== 'true';
    
    console.log('Page load/reload check:', {
      isFirstLoad,
      timestamp: new Date().toISOString()
    });
    
    return isFirstLoad;
  } catch (error) {
    console.error('Error checking for page reload:', error);
    return true; // Assume it's a reload if we can't check properly
  }
};

/**
 * Checks if navigation is coming from welcome page
 * This prevents redirect loops when intentionally navigating from welcome to game
 */
const isComingFromWelcomePage = (): boolean => {
  try {
    // Check both localStorage and sessionStorage for the from_welcome_page flag
    const fromWelcomeLS = localStorage.getItem('from_welcome_page') === 'true';
    const fromWelcomeSS = sessionStorage.getItem('from_welcome_page') === 'true';
    const isFromWelcome = fromWelcomeLS || fromWelcomeSS;
    
    console.log('Coming from welcome page check:', {
      fromWelcomeLS,
      fromWelcomeSS,
      isFromWelcome,
      timestamp: new Date().toISOString()
    });
    
    // If we found the flag, clear it from localStorage to prevent stale state
    if (fromWelcomeLS) {
      localStorage.removeItem('from_welcome_page');
    }
    
    // Keep the session storage version until tab close
    
    return isFromWelcome;
  } catch (error) {
    console.error('Error checking if coming from welcome page:', error);
    return false;
  }
};

/**
 * DEPRECATED: Use OverlayStateCoordinator instead
 * Kept for backward compatibility only
 */
export const determineOverlayState = (isPageLoad: boolean = false): OverlayState => {
  console.warn('determineOverlayState() is deprecated. Use OverlayStateCoordinator.captureStateSnapshot() and determineOverlayState() instead.');
  
  // Get current mode
  const currentMode = (localStorage.getItem('babblegram_game_mode') || 'daily') as 'daily' | 'practice';
  
  // Use the new coordinator for consistent logic
  const snapshot = OverlayStateCoordinator.captureStateSnapshot(currentMode);
  const decision = OverlayStateCoordinator.determineOverlayState(snapshot);
  
  return {
    showInProgress: decision.showInProgress,
    showGameCompletion: decision.showGameCompletion,
    immediateCompletion: decision.immediateCompletion,
    redirectToWelcome: decision.shouldRedirectToWelcome,
    showInstructions: decision.showInstructions,
    showStreak: decision.showStreak
  };
};

export const clearCachedOverlayState = () => {
  console.log('Clearing cached overlay states (preserving completion state)');
  modeAwareStorage.removeCookie('in_progress_overlay_shown');
  // Don't clear game_completion_state during mode switches - it should persist
  modeAwareStorage.removeCookie('immediate_completion');
  modeAwareStorage.removeCookie('redirect_to_welcome');

  const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
  const prefix = currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
  localStorage.removeItem(`${prefix}in_progress_overlay_shown`);
  // Don't clear game_completion_state during mode switches - it should persist
  
  // Also clear coordinator session state
  OverlayStateCoordinator.clearSessionState();
};

/**
 * Forces a redirect to welcome page by setting the appropriate flags
 */
export const forceRedirectToWelcome = () => {
  console.log('Forcing redirect to welcome page');
  modeAwareStorage.setCookie('redirect_to_welcome', 'true');
  
  // Also set session storage flag to indicate this wasn't a natural reload
  try {
    sessionStorage.setItem('babblegram_force_redirect', 'true');
  } catch (error) {
    console.error('Error setting session storage:', error);
  }
};
